import React, { useState } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Chip,
  Stack,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  WorkOutline,
  AssignmentInd,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useCareer } from '../../context/CareerContext';
import JobDialog from '../../components/JobDialog';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CareerDashboard = () => {
  const { jobs, applications, addJob, updateJob, deleteJob, updateApplicationStatus, loading, error } = useCareer();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add');
  const [selectedJob, setSelectedJob] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [viewApp, setViewApp] = useState(null);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (mode = 'add', job = null) => {
    setDialogMode(mode);
    setSelectedJob(job);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedJob(null);
    setDialogMode('add');
  };

  const handleSaveJob = async (jobData) => {
    try {
      if (dialogMode === 'add') {
        await addJob(jobData);
        setSnackbar({
          open: true,
          message: 'Job added successfully!',
          severity: 'success',
        });
      } else {
        await updateJob({ ...jobData, id: selectedJob.id });
        setSnackbar({
          open: true,
          message: 'Job updated successfully!',
          severity: 'success',
        });
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving job:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to save job. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteJob(jobId);
      setSnackbar({
        open: true,
        message: 'Job deleted successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error deleting job:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to delete job. Please try again.',
        severity: 'error',
      });
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
      setSnackbar({
        open: true,
        message: 'Application status updated successfully!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update application status',
        severity: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f6f8fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ borderRadius: 4, p: { xs: 2, md: 4 }, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3, pb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h4" fontWeight={800} sx={{ flex: 1, color: '#1a237e', letterSpacing: 0.5 }}>
              Careers Admin Portal
            </Typography>
            <Tabs value={tabValue} onChange={handleTabChange} textColor="primary" indicatorColor="primary">
              <Tab icon={<WorkOutline sx={{ mr: 1 }} />} iconPosition="start" label="Job Postings" />
              <Tab icon={<AssignmentInd sx={{ mr: 1 }} />} iconPosition="start" label="Applications" />
            </Tabs>
          </Box>
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" fontWeight={700}>
                Manage Job Postings
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog('add')}
                sx={{
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  fontWeight: 700,
                  fontSize: 16,
                  background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)',
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.08)',
                  '&:hover': {
                    background: 'linear-gradient(90deg, #1565c0 0%, #1976d2 100%)',
                  },
                }}
              >
                Add New Job
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(16,30,54,.06)', border: '1px solid #e3e8ee' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f3f6fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Posted Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 16 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((job, idx) => (
                    <TableRow
                      key={job.id}
                      sx={{
                        background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                        transition: 'background 0.2s',
                        '&:hover': { background: '#e3f2fd' },
                      }}
                    >
                      <TableCell sx={{ fontSize: 15 }}>{job.title}</TableCell>
                      <TableCell sx={{ fontSize: 15 }}>{job.department}</TableCell>
                      <TableCell sx={{ fontSize: 15 }}>{job.location}</TableCell>
                      <TableCell sx={{ fontSize: 15 }}>{job.postedDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          color={job.status === 'Active' ? 'success' : 'default'}
                          sx={{ fontWeight: 700, fontSize: 14, px: 1.5, borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            onClick={() => handleOpenDialog('edit', job)}
                            color="primary"
                            sx={{ bgcolor: '#e3f2fd', borderRadius: 2, '&:hover': { bgcolor: '#bbdefb' } }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteJob(job.id)}
                            color="error"
                            sx={{ bgcolor: '#ffebee', borderRadius: 2, '&:hover': { bgcolor: '#ffcdd2' } }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 3 }}>
              Job Applications
            </Typography>
            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(16,30,54,.06)', border: '1px solid #e3e8ee' }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: '#f3f6fa' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Job Title</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Applied Date</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 16 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((application, idx) => {
                    const job = jobs.find(j => j.id === application.jobId);
                    return (
                      <TableRow
                        key={application.id}
                        sx={{
                          background: idx % 2 === 0 ? '#fff' : '#f8fafc',
                          transition: 'background 0.2s',
                          '&:hover': { background: '#e3f2fd' },
                        }}
                      >
                        <TableCell sx={{ fontSize: 15 }}>{application.name}</TableCell>
                        <TableCell sx={{ fontSize: 15 }}>{application.email}</TableCell>
                        <TableCell sx={{ fontSize: 15 }}>{job ? job.title : 'N/A'}</TableCell>
                        <TableCell sx={{ fontSize: 15 }}>{application.appliedDate}</TableCell>
                        <TableCell>
                          <Chip
                            label={application.status}
                            color={
                              application.status === 'New'
                                ? 'info'
                                : application.status === 'Reviewing'
                                ? 'warning'
                                : application.status === 'Accepted'
                                ? 'success'
                                : 'error'
                            }
                            sx={{ fontWeight: 700, fontSize: 14, px: 1.5, borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {/* View Details Button */}
                            <IconButton
                              color="info"
                              sx={{ bgcolor: '#e3f2fd', borderRadius: 2, '&:hover': { bgcolor: '#bbdefb' } }}
                              onClick={() => setViewApp(application)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                            {/* Download Resume Button */}
                            {application.resume ? (
                              <IconButton
                                component="a"
                                href={application.resume}
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                sx={{ bgcolor: '#e3f2fd', borderRadius: 2, '&:hover': { bgcolor: '#bbdefb' } }}
                                download
                              >
                                <DownloadIcon />
                              </IconButton>
                            ) : (
                              <IconButton disabled sx={{ bgcolor: '#f3f6fa', borderRadius: 2 }}>
                                <DownloadIcon />
                              </IconButton>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleUpdateApplicationStatus(application.id, 'Reviewing')}
                              disabled={application.status !== 'New'}
                              sx={{ borderRadius: 2, fontWeight: 600 }}
                            >
                              Review
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              onClick={() => handleUpdateApplicationStatus(application.id, 'Accepted')}
                              disabled={application.status !== 'Reviewing'}
                              sx={{ borderRadius: 2, fontWeight: 600 }}
                            >
                              Accept
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => handleUpdateApplicationStatus(application.id, 'Rejected')}
                              disabled={application.status !== 'Reviewing'}
                              sx={{ borderRadius: 2, fontWeight: 600 }}
                            >
                              Reject
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {applications.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No applications found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <JobDialog
            open={openDialog}
            onClose={handleCloseDialog}
            onSave={handleSaveJob}
            mode={dialogMode}
            job={selectedJob}
          />
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: '100%', fontWeight: 600, fontSize: 16 }}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
          {/* View Application Dialog */}
          <Dialog open={!!viewApp} onClose={() => setViewApp(null)} maxWidth="sm" fullWidth>
            <DialogTitle>Application Details</DialogTitle>
            <DialogContent dividers>
              {viewApp && (
                <Box sx={{ p: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Name:</Typography>
                  <Typography sx={{ mb: 1 }}>{viewApp.name}</Typography>
                  <Typography variant="subtitle1" fontWeight={700}>Email:</Typography>
                  <Typography sx={{ mb: 1 }}>{viewApp.email}</Typography>
                  {viewApp.phone && <><Typography variant="subtitle1" fontWeight={700}>Phone:</Typography><Typography sx={{ mb: 1 }}>{viewApp.phone}</Typography></>}
                  {viewApp.linkedIn && <><Typography variant="subtitle1" fontWeight={700}>LinkedIn:</Typography><Typography sx={{ mb: 1 }}><a href={viewApp.linkedIn} target="_blank" rel="noopener noreferrer">{viewApp.linkedIn}</a></Typography></>}
                  {viewApp.github && <><Typography variant="subtitle1" fontWeight={700}>GitHub:</Typography><Typography sx={{ mb: 1 }}><a href={viewApp.github} target="_blank" rel="noopener noreferrer">{viewApp.github}</a></Typography></>}
                  {viewApp.resume && <><Typography variant="subtitle1" fontWeight={700}>Resume:</Typography><Typography sx={{ mb: 1 }}><a href={viewApp.resume} target="_blank" rel="noopener noreferrer">Download Resume</a></Typography></>}
                  {viewApp.coverLetter && <><Typography variant="subtitle1" fontWeight={700}>Cover Letter:</Typography><Typography sx={{ mb: 1 }}>{viewApp.coverLetter}</Typography></>}
                  {viewApp.experience && <><Typography variant="subtitle1" fontWeight={700}>Experience:</Typography><Typography sx={{ mb: 1 }}>{viewApp.experience}</Typography></>}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewApp(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </Box>
  );
};

export default CareerDashboard;
